import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { keyBy, mapValues, take, union, uniqBy, without } from "lodash";

import { extractDataAndCheckErrorStatus } from "redux/actions/appActions";
import { createMutationThunk } from "redux/rtk/createMutationThunk";

import {
  BACKGROUND_OPERATION_STATUS,
  IBackgroundOperation,
  normalizeBackgroundOperation,
} from "../types";
import {
  cancelBackgroundOperationApiCall,
  fetchBackgroundOperationsApiCall,
  IFetchBackgroundOperationsQuery,
  TFetchBackgroundOperationsOptions,
} from "./api";
import { backgroundOperationStarted } from "./backgroundOperationStarted";
import { backgroundOperationsInitialState } from "./sliceTypes";

const MAX_STARTED_OPERATIONS_TRACKED = 20;

const statusByOperationId = (operations: IBackgroundOperation[]) =>
  mapValues(keyBy(operations, "id"), "status") as Record<
    string,
    BACKGROUND_OPERATION_STATUS
  >;

type TFetchArg = {
  query?: IFetchBackgroundOperationsQuery;
  append?: boolean;
};

type TFetchResult = {
  backgroundOperations: IBackgroundOperation[];
  totalCount: number;
  append: boolean;
};

export const fetchBackgroundOperations = createMutationThunk<
  TFetchArg,
  TFetchResult
>({
  typePrefix: "background_operations/fetchAll",
  actionName: "FetchBackgroundOperations",
  apiCall: async ({ query, append }) => {
    const serverResponse = await fetchBackgroundOperationsApiCall(query);
    const { background_operations, total_count } =
      extractDataAndCheckErrorStatus(serverResponse);
    const normalizedOperations = (background_operations ?? []).map(
      normalizeBackgroundOperation,
    );
    return {
      backgroundOperations: normalizedOperations,
      totalCount: total_count ?? normalizedOperations.length,
      append: append === true,
    };
  },
});

export const fetchBackgroundOperationsAction = (
  query: IFetchBackgroundOperationsQuery = {},
  options: TFetchBackgroundOperationsOptions = {},
) =>
  fetchBackgroundOperations({
    query,
    append: options.append === true,
  });

export const cancelBackgroundOperation = createMutationThunk<
  string,
  IBackgroundOperation
>({
  typePrefix: "background_operations/cancel",
  actionName: "CancelBackgroundOperation",
  apiCall: async (operationId) => {
    const serverResponse = await cancelBackgroundOperationApiCall(operationId);
    const { background_operation } =
      extractDataAndCheckErrorStatus(serverResponse);
    return normalizeBackgroundOperation(background_operation);
  },
  getSuccessMessage: ({ lang }) =>
    lang.containers.jobs.subCategories.backgroundOperations.cancelSuccess,
});

const backgroundOperationsSlice = createSlice({
  name: "background_operations",
  initialState: backgroundOperationsInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBackgroundOperations.pending, (state) => {
        state.isFetchingAll = true;
      })
      .addCase(fetchBackgroundOperations.fulfilled, (state, action) => {
        const { backgroundOperations, totalCount, append } = action.payload;
        state.isFetchingAll = false;
        state.backgroundOperations = append
          ? uniqBy(
              [...state.backgroundOperations, ...backgroundOperations],
              "id",
            )
          : backgroundOperations;
        state.totalCount = totalCount;
        state.statusByOperationId = {
          ...state.statusByOperationId,
          ...statusByOperationId(backgroundOperations),
        };
      })
      .addCase(fetchBackgroundOperations.rejected, (state) => {
        state.isFetchingAll = false;
      })
      .addCase(cancelBackgroundOperation.pending, (state, action) => {
        state.cancellingOperationIds = union(state.cancellingOperationIds, [
          action.meta.arg,
        ]);
      })
      .addCase(cancelBackgroundOperation.fulfilled, (state, action) => {
        const operationId = action.meta.arg;
        const { status } = action.payload;
        state.cancellingOperationIds = without(
          state.cancellingOperationIds,
          operationId,
        );
        state.statusByOperationId[operationId] = status;
        const operation = state.backgroundOperations.find(
          ({ id }) => id === operationId,
        );
        if (operation) {
          operation.status = status;
        }
      })
      .addCase(cancelBackgroundOperation.rejected, (state, action) => {
        state.cancellingOperationIds = without(
          state.cancellingOperationIds,
          action.meta.arg,
        );
      })
      .addCase(
        backgroundOperationStarted,
        (state, action: PayloadAction<{ operation_id: string }>) => {
          const { operation_id } = action.payload;
          state.startedOperationIds = take(
            union([operation_id], state.startedOperationIds),
            MAX_STARTED_OPERATIONS_TRACKED,
          );
          state.statusByOperationId[operation_id] =
            BACKGROUND_OPERATION_STATUS.RUNNING;
        },
      );
  },
});

export default backgroundOperationsSlice.reducer;
